import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Autocomplete,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useLazyGetDDLBranchListQuery, useLazyGetDDLDestinationBranchListQuery } from "store/services/branchApi";
import { useLazyGetDdlRegionListQuery, useLazyGetDDLRestrictedRegionListQuery } from "store/services/LocationsApi/regionApi";
import { exportExcelFile } from "utils/exportExcelFile";
import { useAuth } from "hooks";
import moment from "moment";
import { useLazyDownloadShipmentsQuery } from "store/services/shipmentApi";

const EXCEL_DATA_INITIAL_STATE = {
  dateRange: [],
  vendor: null,
  sourceBranchId: null,
  destinationBranchId: null,
  sourceRegionId: null,
  destinationRegionId: null,
  statuses: [],
};

const SHIPMENT_STATUSES = [
  "Pending",
  "Collected",
  "Consolidation",
  "Loaded",
  "Unloaded",
  "Deconsolidation",
  "CrossDocked",
  "SendOut",
  "Warehousing",
  "Delivered",
  "DeliveryFailed",
  "Return",
  "Cancelled",
];

const ExportShipmentExcel = ({ isBusy = false }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [showSourceBranchForm, setShowSourceBranchForm] = useState(false);
  const [showDestinationBranchForm, setShowDestinationBranchForm] = useState(false);
  const [showSourceRegionForm, setShowSourceRegionForm] = useState(false);
  const [showDestinationRegionForm, setShowDestinationRegionForm] = useState(false);

  const { viewEntity, isVendor, isCustomer } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  const [
    fetchShipments,
    { isLoading: loadingShipments, isFetching: fetchingShipments },
  ] = useLazyDownloadShipmentsQuery();
  const [fetchBranches, { data: branchesData, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();
  const [fetchDestinationBranches, { data: destinationBranchesData, isLoading: loadingDestinationBranches }] =
    useLazyGetDDLDestinationBranchListQuery();
  const [fetchVendors, { data: vendors, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();
  // Source Province — restricted
  const [fetchRestrictedRegions, { data: restrictedRegionsData, isLoading: loadingRestrictedRegions }] =
    useLazyGetDDLRestrictedRegionListQuery();
  const [fetchRegions, { data: regionsData, isLoading: loadingRegions }] =
    useLazyGetDdlRegionListQuery();

  useEffect(() => {
    if (!showDialog) return;
    if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
      fetchVendors({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      fetchBranches({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      fetchDestinationBranches({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      fetchRegions({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      fetchRestrictedRegions({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [
    fetchVendors,
    fetchBranches,
    fetchDestinationBranches,
    fetchDestinationBranches,
    fetchRegions,
    viewEntity.entity._id,
    viewEntity.entity.account._id,
    showDialog,
  ]);

  const handleDownload = async (data) => {
    const {
      dateRange,
      vendor,
      sourceBranchId,
      destinationBranchId,
      sourceRegionId,
      destinationRegionId,
      statuses,
    } = data;

    const fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
    const toDate = moment(dateRange[1]).format("YYYY-MM-DD");

    const payload = {
      ...(showVendorForm && vendor && { vendorId: vendor._id }),
      ...(showSourceRegionForm && sourceRegionId && { sourceRegionId: sourceRegionId._id }),
      ...(showDestinationRegionForm && destinationRegionId && { destinationRegionId: destinationRegionId._id }),
      ...(showSourceBranchForm && sourceBranchId && { sourceBranchId: sourceBranchId._id }),
      ...(showDestinationBranchForm && destinationBranchId && { destinationBranchId: destinationBranchId._id }),
      ...(statuses.length > 0 && { statuses }),
      fromDate,
      toDate,
      page: 1,
    };

    try {
      const responseData = await fetchShipments(payload).unwrap();

      if (!responseData?.data || responseData.data.length === 0) {
        toast.info(
          "No shipment records found for the selected date range and filters."
        );
        return;
      }

      const formattedData = responseData.data.map((row, index) => ({
        SN: index + 1,
        CreatedAt: row.createdAt || "N/A",
        "Shipment ID": row.shipmentId || "N/A",
        "Source Branch": row.sourceBranchName || "N/A",
        "Destination Branch": row.destinationBranchName || "N/A",
        Receiver: row.receiverUserName || row.receiverName || "N/A",
        "Receiver Phone": row.receiverPhone || "N/A",
        Sender:
          !isCustomer
            ? row.customerUserName
              ? `CUS: ${row.customerUserName}`
              : row.vendorShopName
                ? `VEN: ${row.vendorShopName}`
                : "N/A"
            : "N/A",
        "Total Weight (kg)": row.weight || "N/A",
        "COD Amount": row.codAmount || "N/A",
        "Charge Amount": row.chargeAmount || "N/A",
        "Total Amount": row.totalAmounts || "N/A",
        "Shipment Status": row.status || "N/A",
        DeliveredAt: row.deliveredAt
          ? moment(row.deliveredAt).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
      }));

      exportExcelFile({
        sheetData: formattedData,
        fileName: `Shipment_List_${moment().format("YYYYMMDD")}`,
      });

      if (responseData.pagination.total > responseData.data.length) {
        toast.info(
          `Downloaded ${responseData.data.length} of ${responseData.pagination.total} records. ` +
          `Some records were not included due to the maximum limit.`
        );
      } else {
        toast.success("Data downloaded successfully");
      }
    } catch (error) {
      console.error("Error fetching shipment data:", error);
      toast.error("Error fetching shipment data: " + error.message);
    }
    handleReset();
  };

  const handleReset = () => {
    setShowDialog(false);
    setShowVendorForm(false);
    setShowSourceBranchForm(false);
    setShowDestinationBranchForm(false);
    setShowSourceRegionForm(false);
    setShowDestinationRegionForm(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  useEffect(() => {
    if (isVendor && vendors && vendors.length > 0) {
      setValue("vendor", vendors[0]);
    }
  }, [isVendor, vendors, setValue]);

  return (
    <>
      <Tooltip title="Export Excel" arrow>
        <IconButton color="success" onClick={() => setShowDialog(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Shipment Excel"
        loading={loadingShipments || fetchingShipments}
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        sx={{ "& .MuiDialog-paper": { maxWidth: "650px", width: "100%" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
          {/* Vendor Filter Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
              Vendor Filter
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showVendorForm}
                  onChange={(e) => {
                    setShowVendorForm(e.target.checked);
                    if (!e.target.checked) {
                      setValue("vendor", null);
                    }
                  }}
                  icon={<CheckBoxOutlineBlankOutlined />}
                  checkedIcon={<CheckBoxOutlined />}
                  size="small"
                />
              }
              label="Filter by Vendor"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
              }}
            />
          </Box>

          <Divider />

          {/* Source Filters Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
              Source Filters (Select Province OR Branch)
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showSourceRegionForm}
                    onChange={(e) => {
                      setShowSourceRegionForm(e.target.checked);
                      if (e.target.checked) {
                        setShowSourceBranchForm(false);
                        setValue("sourceBranchId", null);
                      }
                    }}
                    icon={<CheckBoxOutlineBlankOutlined />}
                    checkedIcon={<CheckBoxOutlined />}
                    size="small"
                  />
                }
                label="Source Province"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showSourceBranchForm}
                    onChange={(e) => {
                      setShowSourceBranchForm(e.target.checked);
                      if (e.target.checked) {
                        setShowSourceRegionForm(false);
                        setValue("sourceRegionId", null);
                      }
                    }}
                    icon={<CheckBoxOutlineBlankOutlined />}
                    checkedIcon={<CheckBoxOutlined />}
                    size="small"
                  />
                }
                label="Source Branch"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Destination Filters Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
              Destination Filters (Select Province OR Branch)
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showDestinationRegionForm}
                    onChange={(e) => {
                      setShowDestinationRegionForm(e.target.checked);
                      if (e.target.checked) {
                        setShowDestinationBranchForm(false);
                        setValue("destinationBranchId", null);
                      }
                    }}
                    icon={<CheckBoxOutlineBlankOutlined />}
                    checkedIcon={<CheckBoxOutlined />}
                    size="small"
                  />
                }
                label="Destination Province"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showDestinationBranchForm}
                    onChange={(e) => {
                      setShowDestinationBranchForm(e.target.checked);
                      if (e.target.checked) {
                        setShowDestinationRegionForm(false);
                        setValue("destinationRegionId", null);
                      }
                    }}
                    icon={<CheckBoxOutlineBlankOutlined />}
                    checkedIcon={<CheckBoxOutlined />}
                    size="small"
                  />
                }
                label="Destination Branch"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Status Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
              Status Filter
            </Typography>
            <Controller
              name="statuses"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={SHIPMENT_STATUSES}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  disableCloseOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Status (Optional)"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        sx={{
                          m: 0.5,
                          height: 24,
                          "& .MuiChip-label": { fontSize: "0.75rem" },
                        }}
                      />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        icon={<CheckBoxOutlineBlankOutlined fontSize="small" />}
                        checkedIcon={<CheckBoxOutlined fontSize="small" />}
                        sx={{ p: 0.5 }}
                      />
                      <span style={{ fontSize: "0.875rem" }}>{option}</span>
                    </li>
                  )}
                  sx={{ width: "100%" }}
                  ListboxProps={{ style: { maxHeight: 200 } }}
                />
              )}
            />
          </Box>

          {/* Selected Filters Form */}
          {(showVendorForm ||
            showSourceRegionForm ||
            showDestinationRegionForm ||
            showSourceBranchForm ||
            showDestinationBranchForm) && (
              <>
                <Divider />
                <AppForm loading={isBusy}>
                  <AppForm.Content>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {showVendorForm && (
                        <Controller
                          name="vendor"
                          control={control}
                          rules={{
                            required: showVendorForm
                              ? "Please select Vendor."
                              : false,
                          }}
                          render={({ field: { value, onChange } }) => (
                            <BaseAutocomplete
                              fullWidth
                              label="Select Vendor"
                              required={showVendorForm}
                              value={value || null}
                              onChange={(_, data) => onChange(data)}
                              getOptionLabel={(option) =>
                                option
                                  ? `${option?.ownerName} - ${option?.shopName}`
                                  : ""
                              }
                              isOptionEqualToValue={(option, value) =>
                                option?._id === value?._id
                              }
                              options={vendors || []}
                              loading={loadingVendors}
                              loadingText="Loading Vendors..."
                              helperText={errors?.vendor?.message}
                              error={!!errors?.vendor}
                              size="small"
                            />
                          )}
                        />
                      )}

                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {showSourceRegionForm && (
                        <Controller
                          name="sourceRegionId"
                          control={control}
                          rules={{
                            required: showSourceRegionForm
                              ? "Please select Source Province."
                              : false,
                          }}
                          render={({ field: { value, onChange } }) => (
                            <BaseAutocomplete
                              fullWidth
                              label="Select Source Province"
                              required={showSourceRegionForm}
                              value={value || null}
                              onChange={(_, data) => onChange(data)}
                              getOptionLabel={(option) => option?.name || ""}
                              isOptionEqualToValue={(option, value) =>
                                option?._id === value?._id
                              }
                              options={restrictedRegionsData || []}   // ← restricted
                              loading={loadingRestrictedRegions}
                              loadingText="Loading Provinces..."
                              helperText={errors?.sourceRegionId?.message}
                              error={!!errors?.sourceRegionId}
                              size="small"
                              sx={{ minWidth: 280 }}
                            />
                          )}
                        />
                      )}

                        {showSourceBranchForm && (
                          <Controller
                            name="sourceBranchId"
                            control={control}
                            rules={{
                              required: showSourceBranchForm
                                ? "Please select Source Branch."
                                : false,
                            }}
                            render={({ field: { value, onChange } }) => (
                              <BaseAutocomplete
                                fullWidth
                                label="Select Source Branch"
                                required={showSourceBranchForm}
                                value={value || null}
                                onChange={(_, data) => onChange(data)}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                  option?._id === value?._id
                                }
                                options={branchesData || []}
                                loading={loadingBranches}
                                loadingText="Loading Branches..."
                                helperText={errors?.sourceBranchId?.message}
                                error={!!errors?.sourceBranchId}
                                size="small"
                                sx={{ minWidth: 280 }}
                              />
                            )}
                          />
                        )}
                      </Box>

                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {showDestinationRegionForm && (
                          <Controller
                            name="destinationRegionId"
                            control={control}
                            rules={{
                              required: showDestinationRegionForm
                                ? "Please select Destination Province."
                                : false,
                            }}
                            render={({ field: { value, onChange } }) => (
                              <BaseAutocomplete
                                fullWidth
                                label="Select Destination Province"
                                required={showDestinationRegionForm}
                                value={value || null}
                                onChange={(_, data) => onChange(data)}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                  option?._id === value?._id
                                }
                                options={regionsData || []}
                                loading={loadingRegions}
                                loadingText="Loading Provinces..."
                                helperText={errors?.destinationRegionId?.message}
                                error={!!errors?.destinationRegionId}
                                size="small"
                                sx={{ minWidth: 280 }}
                              />
                            )}
                          />
                        )}

                      {showDestinationBranchForm && (
                        <Controller
                          name="destinationBranchId"
                          control={control}
                          rules={{
                            required: showDestinationBranchForm
                              ? "Please select Destination Branch."
                              : false,
                          }}
                          render={({ field: { value, onChange } }) => (
                            <BaseAutocomplete
                              fullWidth
                              label="Select Destination Branch"
                              required={showDestinationBranchForm}
                              value={value || null}
                              onChange={(_, data) => onChange(data)}
                              getOptionLabel={(option) => option?.name || ""}
                              isOptionEqualToValue={(option, value) =>
                                option?._id === value?._id
                              }
                              options={destinationBranchesData || []}  // ← all branches
                              loading={loadingDestinationBranches}
                              loadingText="Loading Branches..."
                              helperText={errors?.destinationBranchId?.message}
                              error={!!errors?.destinationBranchId}
                              size="small"
                              sx={{ minWidth: 280 }}
                            />
                          )}
                        />
                      )}
                      </Box>
                    </Box>
                  </AppForm.Content>
                </AppForm>
              </>
            )}

          <Divider />

          {/* Date Range */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
              Date Range *
            </Typography>
            <Controller
              name="dateRange"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DateRangeToggle
                  {...field}
                  label="Select Date Range"
                  size="small"
                />
              )}
            />
          </Box>
        </Box>
      </AppConfirmDialog>
    </>
  );
};

export default ExportShipmentExcel;